import { useCallback, useMemo, useState } from "react";
import { format, getDaysInMonth, isSameDay, isWeekend } from "date-fns";
import { de } from "date-fns/locale";
import { Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CalendarNavigation } from "@/components/calendar/CalendarNavigation";
import {
  DAY_ABBREV_WIDTH,
  DAY_NUM_WIDTH,
  HEADER_HEIGHT,
  ROW_HEIGHT,
  getDayAbbrev,
  getHolidays,
  useCalendarMonths,
} from "@/components/calendar/useCalendarMonths";
import {
  bookingLabel,
  formatBookingDates,
  formatBookingDrivers,
} from "@/components/vehicles/VehicleBookingDialog";
import {
  bookingCategoryClasses,
  getBookingColorClass,
} from "@/components/vehicles/booking-colors";
import {
  bookingCategoryLabels,
  useVehicleBookingsInRange,
  type Vehicle,
  type VehicleBooking,
} from "@/hooks/useVehicles";
import { cn } from "@/lib/utils";
import {
  DAY_MS,
  assignBookingLanes,
  findBookingConflicts,
} from "@/lib/vehicle-bookings";

const VEHICLE_LANE_WIDTH = 60;
const VEHICLE_HEADER_HEIGHT = 24;
const MAX_DAYS = 31;

/** Zero date number of a day in a (local) month column */
const zeroDate = (month: Date, day: number) =>
  Date.UTC(month.getFullYear(), month.getMonth(), day);

const toIsoDate = (value: number) => new Date(value).toISOString().slice(0, 10);

interface DragState {
  vehicleId: string;
  startMonthIdx: number;
  startDay: number;
  currentMonthIdx: number;
  currentDay: number;
}

interface VehicleCalendarGridProps {
  vehicles: Vehicle[];
  onBookingClick: (booking: VehicleBooking) => void;
  /** Drag (or click) over days in a vehicle column; dates as YYYY-MM-DD */
  onCreateBooking?: (range: {
    vehicleId: string;
    startDate: string;
    endDate: string;
  }) => void;
  headerActions?: React.ReactNode;
}

/**
 * Vehicle bookings in the layout of the project calendar: months side by
 * side, days as rows, one column per vehicle within each month.
 */
export function VehicleCalendarGrid({
  vehicles,
  onBookingClick,
  onCreateBooking,
  headerActions,
}: VehicleCalendarGridProps) {
  // null = all vehicles
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[] | null>(
    null,
  );
  const shownVehicles = useMemo(
    () =>
      selectedVehicleIds
        ? vehicles.filter((vehicle) => selectedVehicleIds.includes(vehicle.id))
        : vehicles,
    [vehicles, selectedVehicleIds],
  );
  const laneCount = Math.max(1, shownVehicles.length);
  const monthWidth =
    DAY_NUM_WIDTH + DAY_ABBREV_WIDTH + laneCount * VEHICLE_LANE_WIDTH + 10;

  const calendar = useCalendarMonths(monthWidth);
  const {
    isMobile,
    scrollAreaRef,
    months,
    renderedMonthCount,
    handleTouchStart,
    handleTouchEnd,
  } = calendar;
  const gridHeaderHeight =
    (isMobile ? 0 : HEADER_HEIGHT) + VEHICLE_HEADER_HEIGHT;

  const rangeFrom = zeroDate(months[0], 1);
  const lastMonth = months[months.length - 1];
  const rangeTo = zeroDate(lastMonth, getDaysInMonth(lastMonth));
  const { data: bookings } = useVehicleBookingsInRange(rangeFrom, rangeTo);
  const conflicts = useMemo(() => findBookingConflicts(bookings), [bookings]);

  const holidays = useMemo(() => {
    const years = new Set(months.map((month) => month.getFullYear()));
    return Array.from(years).flatMap((year) => getHolidays(year));
  }, [months]);
  const isHoliday = (date: Date) => holidays.some((h) => isSameDay(h, date));

  // Lane geometry: fixed pixels on desktop, a share of the width on mobile
  const lanesLeft = DAY_NUM_WIDTH + DAY_ABBREV_WIDTH + 4;
  const laneLeft = (index: number, fraction = 0) =>
    isMobile
      ? `calc(${lanesLeft}px + (100% - ${lanesLeft + 4}px) * ${(index + fraction) / laneCount})`
      : lanesLeft + (index + fraction) * VEHICLE_LANE_WIDTH;
  const laneWidth = (fraction = 1, gap = 4) =>
    isMobile
      ? `calc((100% - ${lanesLeft + 4}px) * ${fraction / laneCount} - ${gap}px)`
      : fraction * VEHICLE_LANE_WIDTH - gap;

  // --- Drag to create ------------------------------------------------------
  const [drag, setDrag] = useState<DragState | null>(null);

  const dragRange = useCallback(
    (state: DragState) => {
      const a = zeroDate(months[state.startMonthIdx], state.startDay);
      const b = zeroDate(months[state.currentMonthIdx], state.currentDay);
      return a <= b ? [a, b] : [b, a];
    },
    [months],
  );

  const finishDrag = () => {
    if (!drag) return;
    const [start, end] = dragRange(drag);
    setDrag(null);
    onCreateBooking?.({
      vehicleId: drag.vehicleId,
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
    });
  };

  // --- Toggles ---------------------------------------------------------------
  const toggleVehicle = (vehicleId: string, checked: boolean) => {
    const current = selectedVehicleIds ?? vehicles.map((v) => v.id);
    const next = checked
      ? [...current, vehicleId]
      : current.filter((id) => id !== vehicleId);
    setSelectedVehicleIds(next.length === vehicles.length ? null : next);
  };

  const vehicleFilter = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={selectedVehicleIds ? "default" : "outline"}
          size="sm"
          data-testid="vehicle-calendar-filter"
        >
          <Truck className="h-4 w-4 mr-1" />
          Vehicles
          <span className="ml-1 text-xs">
            ({shownVehicles.length}/{vehicles.length})
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="start">
        <div className="space-y-2">
          <div className="flex items-center gap-2 border-b pb-2">
            <Checkbox
              id="vehicle-filter-all"
              checked={selectedVehicleIds === null}
              onCheckedChange={(checked) =>
                setSelectedVehicleIds(checked === true ? null : [])
              }
            />
            <label
              htmlFor="vehicle-filter-all"
              className="cursor-pointer text-sm font-medium"
            >
              All
            </label>
          </div>
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {vehicles.map((vehicle) => (
              <div key={vehicle.id} className="flex items-center gap-2">
                <Checkbox
                  id={`vehicle-filter-${vehicle.id}`}
                  checked={
                    selectedVehicleIds?.includes(vehicle.id) ??
                    selectedVehicleIds === null
                  }
                  onCheckedChange={(checked) =>
                    toggleVehicle(vehicle.id, checked === true)
                  }
                />
                <label
                  htmlFor={`vehicle-filter-${vehicle.id}`}
                  className="cursor-pointer truncate text-sm"
                >
                  {vehicle.name}
                </label>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  const legend = (
    <div className="space-y-2 text-xs">
      <div className="font-medium">Bookings</div>
      <div className="grid gap-1.5">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm bg-[hsl(var(--type-live))]" />
          <span>Project booking (project color)</span>
        </div>
        {(
          Object.keys(bookingCategoryClasses) as Array<
            keyof typeof bookingCategoryClasses
          >
        ).map((category) => (
          <div key={category} className="flex items-center gap-2">
            <span
              className={cn(
                "h-2.5 w-2.5 rounded-sm",
                bookingCategoryClasses[category],
              )}
            />
            <span>{bookingCategoryLabels[category]}</span>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm ring-2 ring-red-500" />
          <span>Double booking</span>
        </div>
      </div>
      <p className="text-muted-foreground">
        Drag over days in a vehicle column to add a booking.
      </p>
    </div>
  );

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <div className="flex h-full flex-col">
        <CalendarNavigation
          calendar={calendar}
          legend={legend}
          extraButtons={vehicles.length > 0 ? vehicleFilter : undefined}
          headerActions={headerActions}
        />

        {vehicles.length === 0 ? (
          <p className="p-6 text-center text-muted-foreground">
            No vehicles yet.
          </p>
        ) : (
          <ScrollArea
            ref={scrollAreaRef}
            className="flex-1 [&>[data-radix-scroll-area-viewport]]:!overflow-y-visible"
          >
            <div
              className="flex select-none"
              style={
                isMobile
                  ? undefined
                  : { minWidth: monthWidth * renderedMonthCount }
              }
              onMouseLeave={() => drag && finishDrag()}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              data-testid="vehicle-calendar"
            >
              {months.map((month, monthIdx) => {
                const daysInMonth = getDaysInMonth(month);
                const monthStart = zeroDate(month, 1);
                const monthEnd = zeroDate(month, daysInMonth);
                const selection =
                  drag &&
                  (() => {
                    const [start, end] = dragRange(drag);
                    if (end < monthStart || start > monthEnd) return null;
                    return {
                      startDay: new Date(
                        Math.max(start, monthStart),
                      ).getUTCDate(),
                      endDay: new Date(Math.min(end, monthEnd)).getUTCDate(),
                      laneIndex: shownVehicles.findIndex(
                        (vehicle) => vehicle.id === drag.vehicleId,
                      ),
                    };
                  })();

                return (
                  <div
                    key={monthIdx}
                    className={cn(
                      "relative border-l border-border",
                      isMobile ? "w-full shrink-0" : "shrink-0",
                    )}
                    style={isMobile ? undefined : { width: monthWidth }}
                    data-testid={`vehicle-calendar-month-${format(month, "yyyy-MM")}`}
                  >
                    {/* Month name and vehicle names stay visible while
                        scrolling; opaque and above the booking bars */}
                    <div
                      className="sticky top-0 z-20 bg-background"
                      style={{ height: gridHeaderHeight }}
                      data-testid="vehicle-calendar-header"
                    >
                      {!isMobile && (
                        <div
                          className="flex items-center border-b border-border bg-muted/50 py-2 pl-2 text-sm font-semibold"
                          style={{ height: HEADER_HEIGHT }}
                        >
                          {format(month, "MMMM", { locale: de })}
                        </div>
                      )}
                      <div
                        className="relative border-b border-border bg-muted/30"
                        style={{ height: VEHICLE_HEADER_HEIGHT }}
                      >
                        {shownVehicles.map((vehicle, index) => (
                          <Tooltip key={vehicle.id}>
                            <TooltipTrigger asChild>
                              <span
                                className="absolute top-0 flex h-full items-center justify-center truncate px-0.5 text-[10px] font-medium text-muted-foreground"
                                style={{
                                  left: laneLeft(index),
                                  width: laneWidth(1, 0),
                                }}
                              >
                                {vehicle.name}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              {vehicle.name}
                              {vehicle.licensePlate &&
                                ` · ${vehicle.licensePlate}`}
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </div>
                    </div>

                    {/* Day rows */}
                    {Array.from({ length: MAX_DAYS }, (_, dayIndex) => {
                      const dayNum = dayIndex + 1;
                      const isValidDay = dayNum <= daysInMonth;
                      const date = new Date(
                        month.getFullYear(),
                        month.getMonth(),
                        dayNum,
                      );
                      const isWeekendDay = isValidDay && isWeekend(date);
                      const isHolidayDay = isValidDay && isHoliday(date);
                      return (
                        <div
                          key={dayNum}
                          style={{ height: ROW_HEIGHT }}
                          className={cn(
                            "flex border-b border-border/50",
                            !isValidDay && "bg-muted/30",
                            isWeekendDay && "bg-muted/60",
                            isHolidayDay && "bg-destructive/10",
                          )}
                        >
                          {isValidDay && (
                            <>
                              <span
                                style={{ width: DAY_NUM_WIDTH }}
                                className="flex shrink-0 items-center justify-center text-xs font-medium text-muted-foreground"
                              >
                                {dayNum}
                              </span>
                              <span
                                style={{ width: DAY_ABBREV_WIDTH }}
                                className={cn(
                                  "flex shrink-0 items-center justify-center text-xs",
                                  isWeekendDay && "text-muted-foreground",
                                  isHolidayDay && "text-destructive",
                                )}
                              >
                                {getDayAbbrev(date)}
                              </span>
                              <span className="w-1 shrink-0" />
                              {shownVehicles.map((vehicle) => (
                                <span
                                  key={vehicle.id}
                                  className={cn(
                                    "border-l border-border/30",
                                    isMobile ? "flex-1" : "shrink-0",
                                    onCreateBooking && "cursor-crosshair",
                                  )}
                                  style={
                                    isMobile
                                      ? undefined
                                      : { width: VEHICLE_LANE_WIDTH }
                                  }
                                  onMouseDown={(event) => {
                                    if (!onCreateBooking || event.button !== 0)
                                      return;
                                    event.preventDefault();
                                    setDrag({
                                      vehicleId: vehicle.id,
                                      startMonthIdx: monthIdx,
                                      startDay: dayNum,
                                      currentMonthIdx: monthIdx,
                                      currentDay: dayNum,
                                    });
                                  }}
                                  onMouseEnter={() => {
                                    if (drag?.vehicleId !== vehicle.id) return;
                                    setDrag({
                                      ...drag,
                                      currentMonthIdx: monthIdx,
                                      currentDay: dayNum,
                                    });
                                  }}
                                  onMouseUp={() => drag && finishDrag()}
                                  data-testid={`vehicle-calendar-cell-${vehicle.id}-${toIsoDate(zeroDate(month, dayNum))}`}
                                />
                              ))}
                            </>
                          )}
                        </div>
                      );
                    })}

                    {/* Drag selection */}
                    {selection && selection.laneIndex >= 0 && (
                      <div
                        className="pointer-events-none absolute z-[5] rounded border-2 border-dashed border-primary bg-primary/20"
                        style={{
                          top:
                            gridHeaderHeight +
                            (selection.startDay - 1) * ROW_HEIGHT +
                            2,
                          height:
                            (selection.endDay - selection.startDay + 1) *
                              ROW_HEIGHT -
                            4,
                          left: laneLeft(selection.laneIndex),
                          width: laneWidth(),
                        }}
                      />
                    )}

                    {/* Booking bars */}
                    {shownVehicles.map((vehicle, laneIndex) => {
                      const spans = bookings
                        .filter(
                          (booking) =>
                            booking.vehicleID === vehicle.id &&
                            booking.startDate <= monthEnd &&
                            booking.endDate >= monthStart,
                        )
                        .map((booking) => ({
                          booking,
                          id: booking.id,
                          vehicleID: vehicle.id,
                          startDate: Math.max(booking.startDate, monthStart),
                          endDate: Math.min(booking.endDate, monthEnd),
                        }));
                      const { lanes, laneCount: subLanes } =
                        assignBookingLanes(spans);

                      return spans.map((span) => {
                        const { booking } = span;
                        const startDay = new Date(span.startDate).getUTCDate();
                        const days =
                          Math.round((span.endDate - span.startDate) / DAY_MS) +
                          1;
                        const isStart = booking.startDate >= monthStart;
                        const isEnd = booking.endDate <= monthEnd;
                        const subLane = lanes.get(booking.id) ?? 0;
                        const others = conflicts.get(booking.id) ?? [];
                        const driver = formatBookingDrivers(booking);
                        return (
                          <Tooltip key={booking.id}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => onBookingClick(booking)}
                                className={cn(
                                  "absolute z-10 overflow-hidden px-1 text-left text-[9px] font-medium leading-tight text-white transition-opacity hover:opacity-80",
                                  getBookingColorClass(booking),
                                  isStart && "rounded-t",
                                  isEnd && "rounded-b",
                                  others.length > 0 &&
                                    "ring-2 ring-inset ring-red-500",
                                )}
                                style={{
                                  top:
                                    gridHeaderHeight +
                                    (startDay - 1) * ROW_HEIGHT +
                                    2,
                                  height: days * ROW_HEIGHT - 4,
                                  left: laneLeft(laneIndex, subLane / subLanes),
                                  width: laneWidth(1 / subLanes),
                                }}
                                data-testid={`vehicle-calendar-booking-${booking.id}`}
                              >
                                <span className="block break-words">
                                  {bookingLabel(booking)}
                                </span>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <div className="space-y-1 text-xs">
                                <p className="text-sm font-semibold">
                                  {bookingLabel(booking)}
                                </p>
                                <p>
                                  {vehicle.name}
                                  {vehicle.licensePlate &&
                                    ` · ${vehicle.licensePlate}`}
                                </p>
                                <p>{formatBookingDates(booking)}</p>
                                <p className="text-muted-foreground">
                                  {bookingCategoryLabels[booking.category]}
                                  {driver && ` · Driver: ${driver}`}
                                </p>
                                {others.length > 0 && (
                                  <p className="text-red-500">
                                    Overlaps:{" "}
                                    {others.map(bookingLabel).join(", ")}
                                  </p>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        );
                      });
                    })}
                  </div>
                );
              })}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        )}
      </div>
    </TooltipProvider>
  );
}
